import { Trash2Icon } from "lucide-react";
import React, { useCallback } from "react";
import { buildApiUrl } from "../../lib/utils";

export type ImageValue = File | string | null;

interface Props {
  value: ImageValue;
  onChange: (value: ImageValue) => void;
  height?: string; // optional height override
}

const ImageUploadDragAndDrop: React.FC<Props> = ({
  value,
  onChange,
  height = "200px",
}) => {
  const handleFileSelect = (file: File | null) => {
    onChange(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) onChange(file);
    },
    [onChange]
  );

  const imageSrc =
    value instanceof File
      ? URL.createObjectURL(value)
      : typeof value === "string"
      ? buildApiUrl(value)
      : null;

  return (
    <div
      className="relative border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-gray-50 hover:border-blue-400
      transition-colors cursor-pointer min-h-[200px] flex items-center justify-center overflow-hidden"
      style={{ height }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      {imageSrc ? (
        // ----------- Image Preview -----------
        <div className="relative w-full h-full flex flex-col items-center justify-center">
          <div className="relative w-full h-full rounded-lg overflow-hidden bg-white border shadow-sm">
            <img
              src={imageSrc}
              alt="Uploaded preview"
              className="w-full h-full object-contain"
              onLoad={(e) => {
                if (value instanceof File) {
                  const img = e.target as HTMLImageElement;
                  setTimeout(() => {
                    if (img.src.startsWith("blob:")) {
                      URL.revokeObjectURL(img.src);
                    }
                  }, 1000);
                }
              }}
            />

            {/* Remove image button */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(null);
              }}
              className="absolute -top-0.5 right-0 w-7 h-7 bg-red-500 text-white rounded-full
              flex items-center justify-center hover:bg-red-600 transition-colors shadow"
            >
              <Trash2Icon className="w-4 h-4" />
            </button>

            {/* Add new image icon */}
            <div className="absolute bottom-2 right-2 bg-black/60 text-white p-1 rounded-md text-xs">
              Change Image
            </div>
          </div>

          {value instanceof File && (
            <p className="text-sm text-green-600 mt-2">✓ {value.name}</p>
          )}
        </div>
      ) : (
        // ----------- Upload Placeholder -----------
        <div className="flex flex-col items-center pointer-events-none">
          <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              ></path>
            </svg>
          </div>

          <div className="mb-2">
            <span className="text-gray-900 font-semibold">Click to upload</span>
            <span className="text-gray-500"> or drag and drop</span>
          </div>

          <p className="text-sm text-gray-500">JPG, JPEG, PNG (Max 10MB)</p>
        </div>
      )}

      {/* Invisible file input */}
      <input
        type="file"
        accept="image/jpeg,image/jpg,image/png"
        onChange={(e) => {
          const file = e.target.files?.[0] || null;
          handleFileSelect(file);
          e.target.value = "";
        }}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />
    </div>
  );
};

export default ImageUploadDragAndDrop;
